import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import { SyncPolicyCard } from "../components/mirror/SyncPolicyCard.js";

type MicrosoftConnection = {
  connected_upn: string | null;
  connected_at: string;
  status: "success" | "failed" | null;
  error: string | null;
};

type ConnectedApp = {
  id: string;
  connected_via: string;
  tenant_name: string;
  role_name: string | null;
  created_at: string;
  last_used_at: string | null;
  revoked: boolean;
};

function fmtDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d.toLocaleString() : "—";
}

export function Account() {
  const toast = useToast();
  const { confirm } = useConfirm();
  const [role, setRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // Display name
  const [displayName, setDisplayName] = useState("");
  const [savedDisplayName, setSavedDisplayName] = useState("");
  const [displayNameBusy, setDisplayNameBusy] = useState(false);

  // AI model preference
  type TenantAiModelRow = { model_id: string; status: string };
  const [tenantAiModels, setTenantAiModels] = useState<TenantAiModelRow[]>([]);
  const [aiModelId, setAiModelId] = useState("");
  const [aiSaveState, setAiSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const aiPrefTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Teams membership
  const [myGroups, setMyGroups] = useState<Array<{ id: string; name: string; status: "active" | "inactive" }>>([]);

  // ADR-0134: the user's own connected apps (OAuth grants across all tenants).
  const [connectedApps, setConnectedApps] = useState<ConnectedApp[]>([]);

  // ADR-0155: the person's own Microsoft connection (personal OneDrive lane).
  const [msConnection, setMsConnection] = useState<MicrosoftConnection | null>(null);
  const [msKnown, setMsKnown] = useState(false);
  const [msBusy, setMsBusy] = useState(false);

  const loadMicrosoft = useCallback(async () => {
    try {
      const res = await apiFetch<{ connection: MicrosoftConnection | null }>("/api/tenant/integrations/microsoft/mine");
      setMsConnection(res.connection);
      setMsKnown(true);
    } catch {
      setMsKnown(false);
    }
  }, []);

  // The user-callback redirects here with ?microsoft=…
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const status = searchParams.get("microsoft");
    if (!status) return;
    if (status === "connected") toast.push({ kind: "success", message: "Microsoft account connected." });
    else if (status === "denied") toast.push({ kind: "error", message: "Microsoft sign-in was cancelled." });
    else toast.push({ kind: "error", message: "Microsoft sign-in did not complete. Try again." });
    searchParams.delete("microsoft");
    setSearchParams(searchParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function connectMicrosoft() {
    setMsBusy(true);
    try {
      const res = await apiFetch<{ authorize_url: string }>("/api/tenant/integrations/microsoft/connect", {
        method: "POST",
        body: {}
      });
      window.location.assign(res.authorize_url);
    } catch (e) {
      const message =
        e instanceof ApiRequestError && e.code === "org_consent_required"
          ? "Your Microsoft admin hasn't approved Atlas yet. Personal connections become available after the organization consent."
          : e instanceof Error
            ? e.message
            : String(e);
      toast.push({ kind: "error", message });
      setMsBusy(false);
    }
  }

  async function disconnectMicrosoft() {
    const ok = await confirm({
      title: "Disconnect your Microsoft account?",
      detail: "OneDrive mirroring for you stops. Files already in your OneDrive stay there.",
      confirmLabel: "Disconnect"
    });
    if (!ok) return;
    setMsBusy(true);
    try {
      await apiFetch("/api/tenant/integrations/microsoft/mine", { method: "DELETE" });
      toast.push({ kind: "success", message: "Microsoft account disconnected." });
      await loadMicrosoft();
    } catch (e) {
      const message =
        e instanceof ApiRequestError && e.code === "in_use"
          ? "Remove your OneDrive sync policy first — disconnecting would strand it."
          : e instanceof Error
            ? e.message
            : String(e);
      toast.push({ kind: "error", message });
    } finally {
      setMsBusy(false);
    }
  }

  const loadConnectedApps = useCallback(async () => {
    try {
      const res = await apiFetch<{ connections: ConnectedApp[] }>("/api/auth/me/connections");
      setConnectedApps(res.connections);
    } catch {
      setConnectedApps([]);
    }
  }, []);

  async function disconnectApp(app: ConnectedApp) {
    const ok = await confirm({
      title: "Disconnect this app?",
      detail: `This ends your "${app.connected_via}" connection to ${app.tenant_name} immediately. You'll need to reconnect from the app to use it again.`,
      confirmLabel: "Disconnect",
    });
    if (!ok) return;
    try {
      await apiFetch(`/api/auth/me/connections/${app.id}/revoke`, { method: "POST" });
      toast.push({ kind: "success", message: "App disconnected." });
      await loadConnectedApps();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  useEffect(() => {
    void (async () => {
      try {
        const me = await apiFetch<{ user: { role?: string; entitlement?: string; display_name?: string | null } }>("/api/tenant/me");
        setRole(me.user.entitlement ?? me.user.role ?? null);
        const dn = me.user.display_name ?? "";
        setDisplayName(dn);
        setSavedDisplayName(dn);
      } catch {
        setRole(null);
      }
      try {
        const ai = await apiFetch<{ models: TenantAiModelRow[]; hub_default: string; user_preference: string | null }>(
          "/api/tenant/ai/models"
        );
        setTenantAiModels(ai.models);
        const pref = ai.user_preference;
        const validPref = Boolean(pref && ai.models.some((m) => m.model_id === pref));
        setAiModelId((validPref ? pref! : null) ?? ai.hub_default ?? ai.models[0]?.model_id ?? "");
      } catch {
        setTenantAiModels([]);
        setAiModelId("");
      }
      try {
        const g = await apiFetch<{ groups: Array<{ id: string; name: string; status: "active" | "inactive" }> }>(
          "/api/tenant/me/groups"
        );
        setMyGroups(g.groups);
      } catch {
        setMyGroups([]);
      }
      await loadConnectedApps();
      await loadMicrosoft();
      setLoading(false);
    })();
  }, [loadConnectedApps, loadMicrosoft]);

  async function saveDisplayName() {
    setDisplayNameBusy(true);
    setErr(null);
    try {
      await apiFetch("/api/tenant/account", { method: "PATCH", body: { display_name: displayName.trim() || null } });
      setSavedDisplayName(displayName.trim());
      toast.push({ kind: "success", message: "Display name updated." });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setDisplayNameBusy(false);
    }
  }

  const scheduleSaveAiPreference = useCallback((modelId: string) => {
    if (aiPrefTimerRef.current) clearTimeout(aiPrefTimerRef.current);
    setAiSaveState("idle");
    aiPrefTimerRef.current = setTimeout(() => {
      void (async () => {
        setAiSaveState("saving");
        try {
          await apiFetch("/api/tenant/account/preferences", {
            method: "PATCH",
            body: { preferred_ai_model_id: modelId }
          });
          setAiSaveState("saved");
          toast.push({ kind: "success", message: "AI model preference updated." });
        } catch {
          setAiSaveState("error");
        }
      })();
    }, 500);
  }, [toast]);

  return (
    <PageLayout title="Account">
      {loading ? (
        <div className="card">
          <div className="muted">Loading…</div>
        </div>
      ) : null}

      {!loading ? (
        <div className="card" style={{ display: "grid", gap: 12 }}>
          <div style={{ fontWeight: 700 }}>Display name</div>
          <div className="muted" style={{ fontSize: 13 }}>
            Shown to other users in place of your email address.
          </div>
          <label className="field">
            <span className="label">Display name</span>
            <input
              className="input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g. Jane Smith"
              disabled={displayNameBusy}
            />
          </label>
          {err ? <ErrorState detail={err} /> : null}
          <div className="actions">
            <button
              type="button"
              className="btn btnPrimary"
              disabled={displayNameBusy || displayName.trim() === savedDisplayName}
              onClick={() => void saveDisplayName()}
            >
              {displayNameBusy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      ) : null}

      {!loading ? (
        <div className="card" style={{ display: "grid", gap: 12, marginTop: 16 }}>
          <div style={{ fontWeight: 700 }}>Teams</div>
          {myGroups.length === 0 ? (
            <div className="muted">You are not a member of any teams.</div>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {myGroups.map((g) => (
                <span key={g.id} className={g.status === "inactive" ? "pill pillNeutral" : "pill"}>
                  {g.name}
                  {g.status === "inactive" ? (
                    <span className="muted" style={{ marginLeft: 6 }}>
                      (inactive)
                    </span>
                  ) : null}
                </span>
              ))}
            </div>
          )}
        </div>
      ) : null}

      {!loading && connectedApps.length > 0 ? (
        <div className="card" style={{ display: "grid", gap: 12, marginTop: 16 }}>
          <div style={{ fontWeight: 700 }}>Connected apps</div>
          <div className="muted" style={{ fontSize: 13 }}>
            Personal AI assistants you've connected to Atlas. Disconnecting one ends its access immediately.
          </div>
          <div className="adminList tableWrap">
            <table className="table">
              <thead>
                <tr>
                  <th>App</th>
                  <th>Workspace</th>
                  <th>Role</th>
                  <th>Connected</th>
                  <th>Last used</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {connectedApps.map((a) => (
                  <tr key={a.id} style={a.revoked ? { opacity: 0.55 } : undefined}>
                    <td style={{ fontWeight: 600 }}>{a.connected_via}</td>
                    <td>{a.tenant_name}</td>
                    <td>{a.role_name ?? "—"}</td>
                    <td>{fmtDateTime(a.created_at)}</td>
                    <td>{fmtDateTime(a.last_used_at)}</td>
                    <td>
                      {a.revoked ? (
                        <span className="pill pillBad">Disconnected</span>
                      ) : (
                        <button className="adminListAction isDanger" onClick={() => void disconnectApp(a)}>Disconnect</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {!loading && msKnown ? (
        <div className="card" style={{ display: "grid", gap: 12, marginTop: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontWeight: 700 }}>Microsoft account</div>
            {msConnection ? (
              msConnection.status === "failed" ? (
                <span className="pill pillWarn">Needs attention</span>
              ) : (
                <span className="pill pillOk">Connected</span>
              )
            ) : (
              <span className="pill pillNeutral">Not connected</span>
            )}
          </div>
          {msConnection ? (
            <>
              <div style={{ fontSize: 13 }}>
                {msConnection.connected_upn ?? "Connected"}{" "}
                <span className="muted">since {fmtDateTime(msConnection.connected_at)}</span>
              </div>
              {msConnection.status === "failed" ? (
                <div className="help" style={{ color: "var(--danger-text)" }}>
                  {msConnection.error ?? "Reconnect your Microsoft account."}{" "}
                  <button className="adminListAction" type="button" onClick={() => void connectMicrosoft()}>
                    Reconnect
                  </button>
                </div>
              ) : null}
              <div className="actions">
                <button type="button" className="btn" onClick={() => void disconnectMicrosoft()} disabled={msBusy}>
                  Disconnect
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="muted" style={{ fontSize: 13 }}>
                Connect your Microsoft account to mirror your personal study documents into your own OneDrive.
              </div>
              <div className="actions">
                <button type="button" className="btn btnPrimary" onClick={() => void connectMicrosoft()} disabled={msBusy}>
                  {msBusy ? "Redirecting…" : "Connect Microsoft account"}
                </button>
              </div>
            </>
          )}
        </div>
      ) : null}

      {!loading && msConnection ? (
        <div style={{ marginTop: 16 }}>
          <SyncPolicyCard
            title="Your OneDrive sync"
            apiPath="/api/tenant/document-sync/mine"
            scope="personal"
            editable
            notConnectedHint="Connect your Microsoft account first."
          />
        </div>
      ) : null}

      {!loading && myGroups.filter((g) => g.status === "active").length > 0 && (role === "admin" || role === "builder") ? (
        <div style={{ display: "grid", gap: 12, marginTop: 16 }}>
          {myGroups
            .filter((g) => g.status === "active")
            .map((g) => (
              <SyncPolicyCard
                key={g.id}
                title={`${g.name} — team document sync`}
                apiPath={`/api/tenant/document-sync/teams/${g.id}`}
                scope="team"
                editable
                notConnectedHint="A tenant admin connects Microsoft 365 under Integrations first."
              />
            ))}
        </div>
      ) : null}

      {!loading && tenantAiModels.length > 0 ? (
        <div className="card" style={{ display: "grid", gap: 12, marginTop: 16 }}>
          <div style={{ fontWeight: 700 }}>AI preferences</div>
          <div className="muted" style={{ fontSize: 13 }}>
            Default model for Ask Atlas in reports and operations. You can override per message in the editor.
          </div>
          <label className="field">
            <span className="label">Preferred model</span>
            <select
              className="input"
              value={aiModelId}
              onChange={(e) => {
                const v = e.target.value;
                setAiModelId(v);
                scheduleSaveAiPreference(v);
              }}
            >
              {tenantAiModels.map((m) => (
                <option key={m.model_id} value={m.model_id}>
                  {m.model_id}
                  {m.status === "deprecated" ? " (deprecated)" : ""}
                </option>
              ))}
            </select>
          </label>
          {aiSaveState === "saving" ? <div className="muted">Saving…</div> : null}
          {aiSaveState === "error" ? <ErrorState detail="Could not save AI preference." /> : null}
        </div>
      ) : null}
    </PageLayout>
  );
}
